import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initFirebaseAppCheck } from './firebaseAppCheck';

/**
 * Конфиг Firebase только из REACT_APP_* (см. .env.example).
 * Прод-значения в код не вшиваются — задавайте переменные в .env или CI.
 */
const FIREBASE_ENV_KEYS = [
  ['REACT_APP_FIREBASE_API_KEY', 'apiKey'],
  ['REACT_APP_FIREBASE_AUTH_DOMAIN', 'authDomain'],
  ['REACT_APP_FIREBASE_PROJECT_ID', 'projectId'],
  ['REACT_APP_FIREBASE_STORAGE_BUCKET', 'storageBucket'],
  ['REACT_APP_FIREBASE_MESSAGING_SENDER_ID', 'messagingSenderId'],
  ['REACT_APP_FIREBASE_APP_ID', 'appId'],
];

function buildFirebaseConfig() {
  /** @type {Record<string, string>} */
  const firebaseConfig = {};
  /** @type {string[]} */
  const missing = [];

  for (const [envKey, configKey] of FIREBASE_ENV_KEYS) {
    const raw = process.env[envKey];
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) {
      missing.push(envKey);
    } else {
      firebaseConfig[configKey] = value;
    }
  }

  if (missing.length > 0) {
    throw new Error(
      `[Firebase] Не заданы переменные окружения: ${missing.join(', ')}. ` +
        'Скопируйте .env.example в .env и подставьте значения из Firebase Console ' +
        '(Project settings → Your apps → SDK setup). Перезапустите dev-сервер после изменения .env.'
    );
  }

  return firebaseConfig;
}

const firebaseConfig = buildFirebaseConfig();

/**
 * Домен Auth из конфига (совпадает с REACT_APP_FIREBASE_AUTH_DOMAIN).
 * Письма Firebase ведут на обработчик: https://<authDomain>/__/auth/action
 */
export function getFirebaseAuthDomain() {
  return firebaseConfig.authDomain;
}

/**
 * Action URL для шаблонов писем в консоли Firebase (поле %LINK% / «Action URL»):
 * обработчик out-of-band до редиректа на continueUrl из actionCodeSettings.
 */
export function getFirebaseAuthActionHandlerUrl() {
  return `https://${firebaseConfig.authDomain}/__/auth/action`;
}

// Инициализация Firebase
const app = initializeApp(firebaseConfig);

initFirebaseAppCheck(app);

// Инициализация сервисов
export const auth = getAuth(app);
// Веб-клиенту важнее стабильный realtime, чем offline persistence:
// persistent multi-tab cache может вызывать внутренние assertion-краши SDK.
export const db = initializeFirestore(app, {
  localCache: memoryLocalCache(),
});
export const storage = getStorage(app);

// Провайдеры аутентификации
export const googleProvider = new GoogleAuthProvider();

// Настройка Google провайдера
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

/** Sign in with Apple (Firebase Console → Auth → Apple; также Service ID и домены в Apple Developer). */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

export default app;
